"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "kegerator-keg-yield-calculator",
  "kegerator-beer-line-calculator",
  "energy-cost-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Kegerator Size Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Get the minimum interior dimensions for your kegerator based on keg type, number of kegs, and CO₂ tank size. Includes 12 oz pour count from the Brewers Association keg-volume standards.",
  faqs: [
    {
      question: "What standard keg sizes can fit a home kegerator?",
      answer:
        "Half-barrels (15.5 gal) need a full-size kegerator. Quarter-barrels (7.75 gal) work in most standard units. Sixth-barrels / sixtels (5.16 gal) and Cornelius / corny kegs (5 gal) fit compact and undercounter kegerators.",
    },
    {
      question: "How tall does the interior need to be?",
      answer:
        "The keg height plus about 10 inches for the tap shank, gas line, and clearance. A half-barrel at 23.3\" needs a roughly 33\" interior; a quarter-barrel at 13.9\" needs about 24\".",
    },
    {
      question: "What CO₂ tank size should I pick?",
      answer:
        "A 5 lb CO₂ tank pushes roughly 6–8 half-barrels before refill. 10 lb covers ~15 kegs, 20 lb covers ~30. Bigger tanks need ~2 inches more interior depth.",
    },
    {
      question: "How many 12 oz pours come from one keg?",
      answer:
        "1 US gallon = 128 fl oz = ~10.67 twelve-ounce pours. A half-barrel yields ~165 pours, a sixth-barrel ~55, before pour wastage.",
    },
    {
      question: "Do I need a dual-tap kegerator?",
      answer:
        "Only if you want two beers on at once or pour from two kegs simultaneously. Dual-tap units need ~36\" of width and a wider beverage center compartment.",
    },
  ],
  relatedTools: ["kegerator-keg-yield-calculator", "kegerator-beer-line-calculator", "energy-cost-calculator"],
}

const KEG_DIMENSIONS: Record<string, { height: number; diameter: number; gallons: number; label: string }> = {
  "half-barrel": { height: 23.3, diameter: 16.1, gallons: 15.5, label: "Half barrel (15.5 gal)" },
  "quarter-barrel": { height: 13.9, diameter: 16.1, gallons: 7.75, label: "Quarter barrel (7.75 gal)" },
  "sixth-barrel": { height: 23.3, diameter: 9.25, gallons: 5.16, label: "Sixth barrel / sixtel (5.16 gal)" },
  "cornelius": { height: 23, diameter: 9, gallons: 5, label: "Cornelius / corny (5 gal)" },
}

export function KegeratorSizeCalculatorPageClient() {
  const [kegSize, setKegSize] = useState("half-barrel")
  const [numberOfKegs, setNumberOfKegs] = useState("1")
  const [co2Tank, setCo2Tank] = useState("5lb")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // Kegerator cabinets are built single-, dual-, quad- and six-tap; six is the
    // largest standard configuration.
    const kegs = readCount(numberOfKegs, { min: 1, max: 6 })
    if (kegs === null) return null
    const keg = KEG_DIMENSIONS[kegSize]
    if (keg === undefined) return null

    const minHeight = keg.height + 10
    const minWidth = keg.diameter * kegs + (kegs > 1 ? 4 : 2)
    const co2Depth = co2Tank === "20lb" ? 9 : co2Tank === "10lb" ? 8 : 7
    const minDepth = keg.diameter + co2Depth + 4
    const servings = Math.floor(keg.gallons * kegs * 10.67)

    let recommendedSize: string
    if (kegSize === "half-barrel" && kegs === 1) {
      recommendedSize = "Full-Size Kegerator (24\"W × 33\"H × 25\"D)"
    } else if (kegSize === "sixth-barrel" || kegSize === "cornelius") {
      recommendedSize = "Compact Kegerator (20\"W × 33\"H × 20\"D)"
    } else if (kegs >= 2) {
      recommendedSize = "Commercial Dual-Tap Kegerator (36\"W × 34\"H × 26\"D)"
    } else {
      recommendedSize = "Standard Kegerator (24\"W × 33\"H × 24\"D)"
    }

    return {
      minHeight: Math.round(minHeight * 10) / 10,
      minWidth: Math.round(minWidth * 10) / 10,
      minDepth: Math.round(minDepth * 10) / 10,
      servings,
      recommendedSize,
      kegLabel: keg.label,
    }
  }, [kegSize, numberOfKegs, co2Tank])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Will the keg"
      heroTitleItalic="actually"
      heroTitlePost="fit in there?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~15 sec", "Brewers Association keg dimensions"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "23.3\"", label: "Half-barrel height", note: "Standard" },
            { value: "9.25\"", label: "Sixtel diameter", note: "Compact" },
            { value: "10.67", label: "Pours per gallon", note: "12 oz cups" },
            { value: "+10\"", label: "Tap clearance", note: "Above keg" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "kegerator-size-calculator")}
      cta={{
        title: "Right size kegerator. Right cold box.",
        body: "Browse kegerators sized from compact undercounter sixtels to full-size dual-tap commercial units.",
        buttonLabel: "Browse kegerators",
        buttonHref: "/kegerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Keg type.
                <br />
                Tank size.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Standard US keg sizes. Tap clearance and CO₂ tank depth are already included.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Keg size
                  </label>
                  <select
                    value={kegSize}
                    onChange={(e) => setKegSize(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    {Object.entries(KEG_DIMENSIONS).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Number of kegs
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={6}
                      step="1"
                      value={numberOfKegs}
                      onChange={(e) => setNumberOfKegs(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">kegs</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    CO₂ tank
                  </label>
                  <select
                    value={co2Tank}
                    onChange={(e) => setCo2Tank(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="5lb">5 lb (≈ 6–8 half-barrels)</option>
                    <option value="10lb">10 lb (≈ 15 half-barrels)</option>
                    <option value="20lb">20 lb (≈ 30 half-barrels)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Bigger tank = fewer refills but ~2&quot; more depth.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Minimum interior
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(40px, 5vw, 64px)", letterSpacing: "-0.045em", lineHeight: 0.95 }}
                  >
                    {result?.minWidth ?? 0}<span className="text-white/60 mx-2">×</span>{result?.minHeight ?? 0}<span className="text-white/60 mx-2">×</span>{result?.minDepth ?? 0}
                    <span className="text-[18px] md:text-[22px] font-black tracking-tighter text-white/60 ml-2">in</span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result?.recommendedSize ?? "Pick a keg type to begin."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Width</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.minWidth ?? "—"}&quot;</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Height</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.minHeight ?? "—"}&quot;</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">12 oz pours</div>
                      <div className="text-[16px] sm:text-[20px] font-black tracking-tight">{result?.servings ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the cold box"
          title="Counter-depth fridges that fit a kegerator setup"
          description="Undercounter and freestanding cold boxes with shelving you can pull for keg conversions."
          collectionType="counter-depth"
          fallbackCollectionType="counter-depth"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Real keg specs. <em className="italic text-[#ff9951]">Real</em> clearances.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Keg dimensions match Brewers Association published US keg standards. Pour math uses 128 fl oz / gal ÷ 12 oz pours.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Interior height",
                  d: "Keg height + ~10 inches for tap shank, gas line, and lid clearance.",
                },
                {
                  n: "02",
                  t: "Width and depth",
                  d: "Width = keg diameter × number of kegs + spacing. Depth = keg diameter + CO₂ tank depth + 4 inches.",
                },
                {
                  n: "03",
                  t: "Pour count",
                  d: "Gallons × number of kegs × 10.67 (12 oz pours per gallon). Subtract pour wastage for usable count.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Wine coolers for the bar that drinks both"
          description="If your bar pours wine alongside draft, a dedicated wine cooler keeps temperatures honest."
          collectionType="wine-cooler"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
